import CircularProgress from '@mui/material/CircularProgress';
import { FC, useState } from 'react';
import { CommentMediaType, commentsApi } from 'src/api/comments';
import { UploadImage } from 'src/components/common/UploadImage/UploadImage';
import { MuiModal } from 'src/components/material/MuiModal';
import MaterialTextField from 'src/components/material/textField';
import styles from './ImageModal.module.scss';

interface ImageModalProps {
  isOpenModal: boolean;
  setIsOpenModal: (open: boolean) => void;
  postId: string;
  onMediaUploaded: (fileId: string) => void;
  onCommentPosted?: (newComment: any) => void;
  isReply?: boolean;
  parentCommentId?: string;
}

export const ImageModal: FC<ImageModalProps> = ({
  isOpenModal,
  setIsOpenModal,
  postId,
  onMediaUploaded,
  onCommentPosted,
  isReply,
  parentCommentId,
}) => {
  const [imageId, setImageId] = useState('');
  const [imageText, setImageText] = useState('');
  const [isPosting, setIsPosting] = useState(false);
  const [imageError, setImageError] = useState('');

  const handleImageUpdate = (fileId: string | File) => {
    if (typeof fileId === 'string') {
      setImageId(fileId);
      setImageError('');
      onMediaUploaded(fileId);
    }
  };

  const handleAddClick = async () => {
    if (!imageId && imageText.trim() === '') {
      return;
    }

    try {
      setIsPosting(true);
      const newComment = await commentsApi.postComment(postId, {
        ...(imageText && { text: imageText }),
        ...(imageId && {
          media_type: CommentMediaType.IMAGE,
          file_id: imageId,
        }),
        ...(isReply && parentCommentId ? { parent_id: parentCommentId } : {}),
      });
      if (newComment && onCommentPosted) {
        onCommentPosted(newComment);
      }
      setImageText('');
      setImageId('');
      setIsOpenModal(false);
    } catch (err) {
      setImageError('Failed to send comment. Please try again.');
    } finally {
      setIsPosting(false);
    }
  };

  return (
    <MuiModal open={isOpenModal} setOpen={setIsOpenModal}>
      <UploadImage
        onUpdate={handleImageUpdate}
        helperText="Upload an image"
        defaultImage={''}
        hideDesc
        commentType="image-comment"
      />
      {imageError && <div className={styles.error}>{imageError}</div>}
      <MaterialTextField
        variant="outlined"
        color="primary"
        placeholder="Add a comment"
        value={imageText}
        onChange={(e) => setImageText(e.target.value)}
      />
      <button
        className={styles.btn}
        onClick={handleAddClick}
        disabled={isPosting || !imageId}
      >
        {isPosting ? (
          <CircularProgress size={24} sx={{ color: 'white' }} />
        ) : (
          'Send'
        )}
      </button>
    </MuiModal>
  );
};
